'use client';

import { type ComponentProps } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import { sanityClient } from 'lib/sanity.server';
import {
	type UseNextSanityImageOptions,
	useNextSanityImage,
} from 'next-sanity-image';
import styles from './sanity-image.module.scss';
import { isDev } from 'lib/is-dev';
import {
	type Image as ISanityImageType,
	isExpandedImage,
} from '@metmuseum/types';

const preventUpscaleImageOptions: UseNextSanityImageOptions = {
	imageBuilder: (imageUrlBuilder, options) => {
		const result = imageUrlBuilder
			.quality(options.quality ?? 75) // default quality
			.fit('max'); // prevent upscaling

		return options.width === null ? result : result.width(options.width);
	},
};

const SanityImage = ({
	alt,
	image,
	className,
	sizes = '100vw',
	priority = false,
	quality = 75,
	placeholder = 'empty',
	fill = false,
	draggable = true,
	preventUpscale = false,
	style,
}: ISanityImage) => {
	const imageProps = useNextSanityImage(
		sanityClient({}),
		image,
		preventUpscale ? preventUpscaleImageOptions : undefined,
	);

	// an image object with no resolvable asset (e.g. a field cleared in the Studio,
	// which strips the asset but leaves its siblings behind) yields null imageProps,
	// and rendering next/image without a src throws
	if (!image || !imageProps) {
		return null;
	}

	const altText = alt || image.alt;
	// The asset reference should be resolved with the groqImageResolver for the palette to be available.
	const palette =
		(isExpandedImage(image) && image.asset.metadata?.palette?.dominant) || null;
	if (!palette && isDev()) {
		const message = `An image on this page was not resolved using groqImageResolver \n
		This means alt text authored in the media library is ignored, \n
		and the image won't have the correct loading background color.`;
		console.error(message);
	}

	const colorStyles = palette
		? {
				'--img-bg-color': palette.background,
				'--img-color': palette.foreground,
			}
		: {};

	const handleImageLoaded = (event) => {
		event?.target?.removeAttribute('data-loading');
	};

	return (
		<Image
			className={cn(styles.img, fill ? styles.fill : styles.default, className)}
			alt={altText || ' '}
			style={{ ...colorStyles, ...style }}
			{...imageProps}
			fill={fill}
			quality={quality}
			data-loading={true}
			placeholder={placeholder}
			priority={priority}
			sizes={sizes}
			onLoad={handleImageLoaded}
			width={fill ? 0 : imageProps.width}
			height={fill ? 0 : imageProps.height}
			draggable={draggable}
		/>
	);
};

type ImageProps = ComponentProps<typeof Image>;

interface ISanityImage {
	alt?: ImageProps['alt'];
	className?: ImageProps['className'];
	fill?: ImageProps['fill'];
	image: ISanityImageType;
	placeholder?: ImageProps['placeholder'];
	preventUpscale?: boolean;
	priority?: ImageProps['priority'];
	quality?: ImageProps['quality'];
	sizes?: ImageProps['sizes'];
	style?: ImageProps['style'];
	loading?: ImageProps['loading'];
	draggable?: ImageProps['draggable'];
}

export default SanityImage;
